import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { orange, black, textMuted, mutedGrey, white, surface2, border1 } from '@/theme/colors'
import { shadows, radii, layers } from '@/theme/tokens'
import { fontSizes } from '@/theme/typography'
import { Button } from '@components/Button'
import { Checkbox } from '@components/Field'

interface DaysFilterProps {
	dayCount: number
	selectedDays: Set<number>
	mappedCountByDay: Map<number, number>
	onChange: (next: Set<number>) => void
}

const Container = styled.div`
	position: relative;
	padding: 12px 20px;
	flex-shrink: 0;
	border-bottom: 1px solid ${border1};
`

const TriggerButton = styled(Button).attrs({ variant: 'secondary' as const, size: 'sm' as const })``

const TriggerLabel = styled.span``

const Caret = styled.span`
	font-size: ${fontSizes.micro};
	color: ${mutedGrey};
`

const Count = styled.span<{ $active: boolean }>`
	font-size: ${fontSizes.micro};
	padding: 2px 8px;
	border-radius: ${radii.inner};
	background: ${props => (props.$active ? orange : border1)};
	color: ${props => (props.$active ? 'white' : textMuted)};
`

const Popover = styled.div`
	position: absolute;
	top: calc(100% - 4px);
	left: 20px;
	z-index: ${layers.float};
	background: ${white};
	border: 1px solid ${border1};
	border-radius: ${radii.inner};
	box-shadow: ${shadows.overlay};
	min-width: 240px;
	max-height: 360px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
`

const PopoverActions = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 10px 14px;
	border-bottom: 1px solid ${border1};
`

const ActionLink = styled(Button).attrs({ variant: 'ghost' as const, size: 'sm' as const })`
	padding-top: 0;
	padding-bottom: 0;
`

const DayList = styled.div`
	overflow-y: auto;
	padding: 6px 0;
`

const DayRow = styled.label<{ $disabled: boolean }>`
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 14px;
	cursor: ${props => (props.$disabled ? 'not-allowed' : 'pointer')};
	opacity: ${props => (props.$disabled ? 0.5 : 1)};
	font-size: ${fontSizes.small};
	color: ${black};

	&:hover {
		background: ${props => (props.$disabled ? 'transparent' : surface2)};
	}

	input {
		cursor: inherit;
		accent-color: ${orange};
	}
`

const DayLabel = styled.span`
	flex: 1;
`

const DaysFilter: React.FC<DaysFilterProps> = ({ dayCount, selectedDays, mappedCountByDay, onChange }) => {
	const [open, setOpen] = useState(false)
	const containerRef = useRef<HTMLDivElement | null>(null)

	useEffect(() => {
		if (!open) return
		const handleClick = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false)
			}
		}
		const handleKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('mousedown', handleClick)
		document.addEventListener('keydown', handleKey)
		return () => {
			document.removeEventListener('mousedown', handleClick)
			document.removeEventListener('keydown', handleKey)
		}
	}, [open])

	const allDayIndices = Array.from({ length: dayCount }, (_, i) => i)
	const availableDays = allDayIndices.filter(i => (mappedCountByDay.get(i) ?? 0) > 0)
	const allSelected = availableDays.length > 0 && availableDays.every(i => selectedDays.has(i))

	const toggleDay = (dayIndex: number) => {
		const next = new Set(selectedDays)
		if (next.has(dayIndex)) {
			if (next.size === 1) return
			next.delete(dayIndex)
		} else {
			next.add(dayIndex)
		}
		onChange(next)
	}

	const selectAll = () => onChange(new Set(availableDays))
	const clearAll = () => {
		if (availableDays.length === 0) return
		onChange(new Set([availableDays[0]]))
	}

	const label = (() => {
		if (selectedDays.size === 0) return 'No days'
		if (selectedDays.size === 1) {
			const only = [...selectedDays][0]
			return `Day ${only + 1}`
		}
		if (allSelected) return 'All days'
		return `${selectedDays.size} days`
	})()

	return (
		<Container ref={containerRef}>
			<TriggerButton type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}>
				<TriggerLabel>Filter: {label}</TriggerLabel>
				<Caret>▾</Caret>
			</TriggerButton>
			{open && (
				<Popover role="dialog">
					<PopoverActions>
						<ActionLink type="button" onClick={selectAll} disabled={allSelected}>
							Select all
						</ActionLink>
						<ActionLink
							type="button"
							onClick={clearAll}
							disabled={selectedDays.size <= 1}
						>
							Clear
						</ActionLink>
					</PopoverActions>
					<DayList>
						{allDayIndices.map(dayIndex => {
							const count = mappedCountByDay.get(dayIndex) ?? 0
							const disabled = count === 0
							const checked = selectedDays.has(dayIndex)
							return (
								<DayRow key={dayIndex} $disabled={disabled}>
									<Checkbox
										checked={checked}
										disabled={disabled}
										onChange={() => toggleDay(dayIndex)}
									/>
									<DayLabel>Day {dayIndex + 1}</DayLabel>
									<Count $active={checked}>{count}</Count>
								</DayRow>
							)
						})}
					</DayList>
				</Popover>
			)}
		</Container>
	)
}

export default DaysFilter
